/*
 * File: catalog.service.ts                                                    *
 * Project: catalog-cloud                                                      *
 * File Created: Wednesday, 22nd December 2021 12:48:17                        *
 * Author: Tomás Muniesa (tomas@cege.es)                                       *
 * -----                                                                       *
 * Last Modified: Thursday, 2nd March 2023 18:03:57                            *
 * Modified By: Tomás Muniesa (tomas@cege.es>)                                 *
 * -----                                                                       *
 * Copyright 2021 - 2022 Nousmedis, CeGe                                       *
 */

import { Injectable } from '@angular/core';
import { firstValueFrom, Observable, ReplaySubject, Subject } from 'rxjs';

import { ActionsMenuService } from '../../menus/services/actionsMenu.service';
import { ApiService } from '../../api/services/api.service';
import { Catalog } from '@nousmedis/cc-types';
import { CatalogForm } from '../interfaces/catalog-form.interface';
import { CatalogExtra } from '@nousmedis/cc-types';
import { CatalogUploadComponent } from '../components/catalog/catalog-upload/catalog-upload.component';
import { CatalogFormEditComponent } from '../components/catalog/catalog-form-edit/catalog-form-edit.component';

import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'environment';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { CatalogFormComponent } from '../components/catalog/catalog-form/catalog-form.component';
import { FascicleService } from './fascicle.service';
import { Router } from '@angular/router';
import JSZip from '../../../../../node_modules/jszip'; //arreglar esto
import { CatalogFormWizardComponent } from '../components/catalog/catalog-form-wizard/catalog-form-wizard.component';
import { CatalogSyncComponent } from '../components/catalog/catalog-sync/catalog-sync.component';

@Injectable()
export class CatalogService {
  /**
   * Variables for datatables
   */
  private dataSubject = new Subject<Catalog[]>();
  data$: Observable<Catalog[]> = this.dataSubject.asObservable();
  currentCatalog$: Subject<Catalog> = new Subject<Catalog>();
  dataLoading: Subject<boolean> = new Subject<boolean>();
  refreshData: Subject<boolean> = new Subject<boolean>();

  protected get urlBase(): string { return this.api.getBaseUrl(); }

  get endPointCatalog(): string { return this.urlBase + '/api/v1/catalogs'; }

  get endpoints(): any {
    return {
      testImport: this.endPointCatalog + '/check-import',
      testExport: this.endPointCatalog + '/check-export',
    };
  }

  noPagination = '?pagination=false';

  selectedCatalogs: Array<string> = [];

  allDeleted: Subject<number> = new Subject<number>();
  ref: DynamicDialogRef | undefined;

  catalogId: string;

  constructor (
    private fascicleService: FascicleService,
    private router: Router,
    private api: ApiService,
    private modalDialog: DialogService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private actionMenuService: ActionsMenuService,
    private trans: TranslateService
  ) { }

  /**
   * Se inicia un objeto del tipo Catalog-Form
   * @returns
   */
  initializeForm(): CatalogForm {
    return {
      mainFields: {
        id: undefined,
        createdAt: new Date(),
        createdBy: '',
        updateBy: '',
        updatedAt: new Date(),
        name: '',
        title: '',
        connector: '',
        privateKey: '',
      },
      extraFields: {
        extra: [],
      },
    };
  }

  initializeCatalog(): Catalog {
    return {
      id: undefined,
      name: '',
      title: '',
      frontImage: '',
      backImage: '',
      importCredentials: {
        url: '',
        token: '',
      },
      exportCredentials: {
        url: '',
        token: '',
      },
      syncronized: false,
      lastSyncro: new Date(),
      fascicles: [],
      tags: [],
      categories: [],
      products: [],
      extra: [null],
      createdAt: new Date(),
      createdBy: '',
      updatedAt: new Date(),
      updatedBy: '',
    };
  }

  /**
   * Get catalog from server
   * @param id id Catalog
   * @returns
   */
  get(id: string) {
    if (id != '') {
      return this.api.get(this.endPointCatalog + '/' + id);
    } else {
      return new Observable((observer) => {
        //let data = this.intialize();
        // observer.next(data);
      });
    }
  }

  /**
   * Convert the data from the form to a compatible entity for backend
   * @param form data form from the catalog-form component
   * @returns
   */
  private mapFormToCatalogModel(form: FormGroup): Catalog {
    let catalog: Catalog = this.initializeCatalog();
    let catalogKeys = Object.keys(catalog);

    let mainFields: FormGroup = form.get('mainFields') as FormGroup;
    let extraFields: FormGroup = form.get('extraFields') as FormGroup;

    //Mapear todas las propiedades del catalogo
    Object.keys(mainFields.controls).forEach((key) => {
      if (catalog.hasOwnProperty(key)) {
        (catalog as any)[key] = mainFields.controls[key]?.value;
      }
    });

    this.exportCredentials(catalog, mainFields);
    this.importCredentials(catalog, mainFields);

    //Se recogen datos dinámicos insertados por el usuario
    for (var field in extraFields.controls) {
      var extraField: CatalogExtra = {
        name: field,
        value: extraFields.controls[field].value,
        label: field.replace(/_/g, ' '),
      };
      catalog.extra.push(extraField);
    }

    return catalog;
  }

  /**
   * Comportamiento del formulario si se abre en modo de diálogo.
   * Ese formulario abre el wizard de edición de catálogo.
   * @param id
   */
  openForm(id: string) {
    const titulo = !id ? this.trans.instant('CATALOG.CREATE') : this.trans.instant('CATALOG.EDIT');
    this.modalDialog
      .open(CatalogFormWizardComponent, {
        header: titulo,
        maximizable: true,
        modal: true,
        style: { 'min-width': '70rem' },
        closeOnEscape: true,
        data: {
          id: id,
        },
      })
      .onClose.subscribe((wizardForm) => {
        this.fetchCatalogList();
        this.actionMenuService.reset();
      });

    // this.modalDialog
    //   .open(ModalDialogComponent, {
    //     width: '954px',
    //     height: 'auto',
    //     closeOnNavigation: false,
    //     disableClose: true,
    //     panelClass: '',
    //     data: {
    //       title:
    //         id != ''
    //           ? this.trans.instant('CATALOG.EDIT')
    //           : this.trans.instant('CATALOG.CREATE'),
    //       component: CatalogWizardComponent,
    //       id: id,
    //       type: 'cancel',
    //     },
    //   })
    //   .afterClosed()
    //   .subscribe((wizardForm) => {
    //     this.fetchCatalogList();
    //     this.actionMenuService.reset();
    //   });
  }

  openEditForm(id: string) {
    this.router.navigate(['/catalog', id, 'edit']);
  }

  openSyncForm(id: string) {
    const titulo = this.trans.instant('CATALOG.SYNC_TO_SW');
    this.modalDialog
      .open(CatalogSyncComponent, {
        header: titulo,
        maximizable: false,
        modal: true,
        style: { 'min-width': '50rem' },
        closeOnEscape: true,
        data: {
          id: id,
        },
      })
      .onClose.subscribe((wizardForm) => {
        this.fetchCatalogList();
        this.actionMenuService.reset();
      });
  }

  /**
   * Save a catalog
   * @param form
   * @returns
   */
  save(formOrCatalog: FormGroup | Catalog) {
    let model: Catalog;
    if (formOrCatalog instanceof FormGroup) {
      model = this.mapFormToCatalogModel(formOrCatalog);
    } else {
      model = { ...formOrCatalog } as Catalog;
    }
    if (!model.id || model.id.length == 0) {
      delete model.exportCredentials;
      delete model.importCredentials;
      delete model.updatedBy;
      return this.api.post(this.endPointCatalog, model);
    } else {
      delete model.fascicles;
      delete model.createdAt;
      delete model.createdBy;
      delete model.categories;
      delete model.products;
      delete model.fascicles;
      delete model.tags;

      return this.api.put(this.endPointCatalog + '/' + model.id, model);
    }
  }

  /**
   * Delete an array of catalogs
   * @param ids
   */

  delete(ids: Array<string>) {
    let i = 0;
    for (const id of ids) {
      this.api
        .delete(this.endPointCatalog + '/' + id)
        .subscribe((data: any) => {
          i++;
          this.allDeleted.next(i);
        });
    }
  }

  deleteCatalog(idCatalog: string, event?: Event) {
    this.confirmationService.confirm({
      target: (event?.target ?? undefined) as EventTarget,
      message: this.trans.instant('CATALOG.REMOVE'),
      header: this.trans.instant('CATALOG.REMOVE'),
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check',
      acceptLabel: this.trans.instant('FORM.YES'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.delete([idCatalog]);
      },
      reject: () => { },
    });
  }

  /*
    openSyncForm(id: string) {
    this.modalDialog.open(CatalogFormEditComponent, {
      width: '954px',
      height: 'auto',
      data: {
        title: this.trans.instant('CATALOG.SYNC_TO_SW'),
        component: CatalogFormEditComponent,
        idCatalog: id,
        type: 'cancel',
      },
    });
  }

  openUploadForm(id: string) {
    this.modalDialog.open(CatalogUploadComponent, {
      width: '30%',
      header: this.trans.instant('CATALOG.EXPORT_TO_SL'),
      data: {
        component: CatalogUploadComponent,
        idCatalog: id,
        type: 'cancel',
      },
    });
  }

    uploadToSs(id: string) {
    return this.api.get(this.endPointCatalog + '/' + id + '/export');
  }

  checkUploadToSs(id: string) {
    return this.api.get(this.endPointCatalog + '/' + id + '/check-export');
  }
  
  */
  openUploadFormToSyncwall(id: string) {
    //implementar logica de subida a syncwall
    const titulo = this.trans.instant('CATALOG.EXPORT_TO_SW');
    this.modalDialog.open(CatalogUploadComponent, {
      header: titulo,
      maximizable: true,
      modal: true,
      style: { 'min-width': '50rem' },
      closeOnEscape: true,
      data: {
        id: id,
      },
    });
  }

  /***
   * Functions for catalg-list
   */

  getDataSource() {
    return this.api.get(this.endPointCatalog);
  }

  fetchCatalogList() {
    this.dataLoading.next(false);
    this.api.get(this.endPointCatalog).subscribe((res) => {
      this.dataSubject.next(res);
      this.dataLoading.next(true);
    });
  }

  getCatalog(id: string) {
    return this.api.get(this.endPointCatalog + '/' + id);
  }

  calcTotalPages(catalog: Catalog) {
    let totalPages = 0;
    catalog.fascicles.forEach((f) => {
      totalPages = totalPages + f.totalPages;
    });
    return totalPages;
  }

  fillDataTable(data: Catalog[]) {
    let dataTable: any = [];
    for (let catalog of data) {
      dataTable.push({
        name: catalog.name,
        id: catalog.id,
        created: catalog.createdAt,
        modified: catalog.updatedAt,
        createdBy: catalog.createdBy,
        fascicles: catalog.fascicles.length,
        pages: this.calcTotalPages(catalog),
        menuItems: [
          {
            icon: 'pi pi-pencil',
            label: this.trans.instant('CATALOG.CONFIGURE'),
            command: () => this.openEditForm(catalog.id),
          },
          {
            icon: 'pi pi-sync',
            label: this.trans.instant('CATALOG.SYNC'),
            command: () => this.openSyncForm(catalog.id),
          },
          {
            icon: 'pi pi-plus',
            label: this.trans.instant('FASCICLE.NEW_FASCICLE'),
            command: () => this.fascicleService.newModalFascicle(catalog.id),
          },
          {
            icon: 'fa-solid fa-wand-magic-sparkles',
            label: this.trans.instant('FASCICLE.AUTOMATIC_GENERATION'),
            command: () => this.fascicleService.newModalFascicleAuto(catalog.id),
          },
          { separator: true },
          {
            icon: 'pi pi-file-export',
            label: this.trans.instant('CATALOG.EXPORT_TO_SW'),
            command: () => this.openUploadFormToSyncwall(catalog.id),
          },
          {
            icon: 'pi pi-download',
            label: this.trans.instant('MENU.EXPORT_CSV'),
            command: () => this.checkExportToCSV(catalog.id),
          },
          { separator: true },
          {
            icon: 'pi pi-list',
            label: this.trans.instant('CATALOG.INDEX'),
            command: () => this.router.navigate(['/catalog', catalog.id, 'index']),
          },
          { separator: true },
          {
            icon: 'pi pi-trash',
            label: this.trans.instant('CATALOG.DELETE'),
            styleClass: 'text-red-500',
            command: () => this.deleteCatalog(catalog.id),
          },
        ] as MenuItem[],
      });
    }
    return dataTable;
  }

  setCurrentCatalog(id: string) {
    this.getCatalog(id).subscribe((catalog) => {
      this.currentCatalog$.next(catalog);
    });
  }

  /**
   * Clone Functions
   */
  clone(id: string) {
    let cloneModel = {
      modelCatalogReference: id,
    };
    this.api
      .post(this.endPointCatalog + '/clone/' + id, cloneModel)
      .subscribe((data) => {
        this.messageService.add({
          severity: 'info',
          summary: this.trans.instant('CATALOG.DUPLICATED'),
          detail: this.trans.instant('CATALOG.DUPLICATED'),
        });
        this.fetchCatalogList();
        this.actionMenuService.reset();
      });
  }

  /**
   * Check in and out catalog functions
   * @param id
   */
  async checkImportExport(data: any): Promise<{
    success: boolean;
    importCheck?: any;
    exportCheck?: any;
    error?: any;
  }> {
    const importUrl = data.importUrl;
    const privateImportKey = data.privateImportKey;
    const exportUrl = data.exportUrl;
    const privateExportKey = data.privateExportKey;

    const importData = {
      url: importUrl,
      token: privateImportKey,
    };

    const exportData = {
      url: exportUrl,
      token: privateExportKey,
    };

    try {
      const importCheck = await firstValueFrom(
        this.api.post(this.endpoints.testImport, importData)
      );
      console.log('Import Check:', importCheck);
      const exportCheck = await firstValueFrom(
        this.api.post(this.endpoints.testExport, exportData)
      );

      // devolver true si es exitoso el import y el export
      return { success: importCheck && exportCheck, importCheck, exportCheck };
    } catch (error) {
      console.error('Error en checkImportExport:', error);

      //Devolver false si hay un error
      return { success: false, error };
    }
  }

  // Check import function
  async checkImport(data: {
    importUrl: string;
    privateImportKey: string;
  }): Promise<{
    success: boolean;
    importCheck?: any;
    error?: any;
  }> {
    const importData = {
      url: data.importUrl,
      token: data.privateImportKey,
    };

    try {
      const importCheck = await firstValueFrom(
        this.api.post(this.endpoints.testImport, importData)
      );
      return { success: true, importCheck };
    } catch (error) {
      console.error('Error en checkImport:', error);
      return { success: false, error };
    }
  }
  //Check export function
  async checkExport(data: {
    exportUrl: string;
    privateExportKey: string;
  }): Promise<{
    success: boolean;
    exportCheck?: any;
    error?: any;
  }> {
    const exportData = {
      url: data.exportUrl,
      token: data.privateExportKey,
    };

    try {
      const exportCheck = await firstValueFrom(
        this.api.post(this.endpoints.testExport, exportData)
      );
      return { success: true, exportCheck };
    } catch (error) {
      console.error('Error en checkExport:', error);
      return { success: false, error };
    }
  }

  firstImport(idCatalog: string) {
    return this.api.post(this.endPointCatalog + '/first-import/', {
      idCatalog: idCatalog,
    });
  }

  checkDifferencesSalesLayer(idCatalog: string) {
    return this.api.get(this.endPointCatalog + '/' + idCatalog + '/sync');
  }

  syncronizeSalesLayer(idCatalog: string) {
    return this.api.post(
      this.endPointCatalog + '/' + idCatalog + '/sync',
      null
    );
  }

  exportCsv(idCatalog: string) {
    var zip: JSZip =
      typeof (<any>JSZip).default === 'function'
        ? new (<any>JSZip).default()
        : undefined;
    var zip = new JSZip();

    firstValueFrom(
      this.api.get(this.endPointCatalog + '/' + idCatalog + '/csv')
    ).then(
      async (data) => {
        const BOM = '\uFEFF';
        let head = data.head;
        let lines = data.lines;
        zip.file('head_' + idCatalog + '.csv', BOM + head);
        zip.file('lines_' + idCatalog + '.csv', BOM + lines);
        zip.generateAsync({ type: 'base64' }).then((zipped: any) => {
          var downloader = document.createElement('a');
          downloader.setAttribute(
            'href',
            'data:text/plain;base64;charset=utf-8,' + zipped
          );
          downloader.setAttribute(
            'download',
            'csv_schema_catalog_' + idCatalog + '.zip'
          );
          downloader.click();
        });

        // this.actionsService.reset();
      },
      (error) => { }
    );
  }

  checkExportToCSV(idCatalog: string) {
    this.confirmationService.confirm({
      message: '¿Quieres exportar el catálogo a CSV?',
      header: 'Exportar catálogo',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.exportCsv(idCatalog);
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.trans.instant('CATALOG.EXPORT_CANCELLED'),
          detail: this.trans.instant('CATALOG.NO_ACTION'),
        });
      },
    });
  }

  async getBreadCrumb(idCatalog: string) {
    return new Promise((resolve, reject) => {
      this.trans.get('CATALOG.LIST').subscribe((res) => {
        resolve([{ label: res, url: '/catalog/list' }]);
      });
    });
  }

  async getBreadCrumbNewCatalog() {
    return new Promise((resolve, reject) => {
      this.trans.get(['CATALOG.LIST', 'CATALOG.CREATE']).subscribe((res) => {
        resolve([
          { label: res['CATALOG.LIST'], url: '/catalog/list' },
          { label: res['CATALOG.CREATE'] },
        ]);
      });
    });
  }

  async getProductTable(id: string) {
    return firstValueFrom(
      this.api.get(this.endPointCatalog + '/' + id + '/' + 'getproducttable')
    );
  }

  //mapear import e export credentials
  exportCredentials(catalog: Catalog, mainFields: FormGroup) {
    catalog.exportCredentials = {
      url: mainFields.get('exportUrl')?.value || '',
      token: mainFields.get('privateExportKey')?.value || '',
    };
  }

  importCredentials(catalog: Catalog, mainFields: FormGroup) {
    catalog.importCredentials = {
      url: mainFields.get('importUrl').value || '',
      token: mainFields.get('privateImportKey').value || '',
    };
  }

  async reSync(id: string) {
    return firstValueFrom(
      this.api.get(this.endPointCatalog + '/' + id + '/sync')
    );
  }

  sendSyncData(id: string, tableData: any) {
    return firstValueFrom(
      this.api.post(this.endPointCatalog + '/' + id + '/sync', tableData)
    );
  }

  async exportToSW(id: string) {
    return await firstValueFrom(
      this.api.get(this.endPointCatalog + '/' + id + '/export')
    );
  }
}
